import React, { useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";
import CartItem from "../../../helper/images/menuimage.png";
import { MdStar } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { getCartPrice, placeOrder } from "../../../api/user/hotels";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useTranslation } from "../../../hooks/useTranslation";

const CartStep = () => {
    const location = useLocation();
    const { cartItems, bookingId, hotelId, roomId, guest } = location.state || {};
    const [cart, setCart] = useState(cartItems || []);
    const [debouncedCart, setDebouncedCart] = useState(cart);
    const [showInstructions, setShowInstructions] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);
    const [formData, setFormData] = useState({
        specialInstructions: "",
        deliveryTime: "",
        deliveryMethod: "deliver to room",
    });
    const navigate = useNavigate()
    const { t } = useTranslation();

    const { data, isLoading } = useQuery({
        queryKey: ["restaurant", bookingId, hotelId, roomId, debouncedCart],
        queryFn: () => getCartPrice({ bookingId, items: debouncedCart, hotelId, roomId }),
        enabled: !!cart.length && (!!bookingId || (!!hotelId && !!roomId)),
        keepPreviousData: true,
    });




    const bookingMutation = useMutation({
        mutationFn: (data) => placeOrder(data),
        onSuccess: ({ data }) => {
            const { url } = data
            if (url) {
                window.open(url, "_blank");
            }

            setTimeout(() => {
                navigate("/user-orders", { replace: true });
            }, 3000);

        },
        onError: (error) => {
            const errorMessage =
                error?.response?.data?.message || t("cart").failedToGetCheckoutUrl || "Failed to get checkout url.";
            toast.error(errorMessage);
        },
    });

    const charges = data?.data?.charges;

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedCart(cart);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [cart]);

    const increment = (id) => {
        setCart((prev) =>
            prev.map((item) =>
                item.itemId === id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            )
        );
    };

    const decrement = (id) => {
        setCart((prev) =>
            prev.map((item) =>
                item.itemId === id && item.quantity > 1
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            )
        );
    };

    const handleSubmit = () => {
        bookingMutation.mutate({ bookingId, hotelId, roomId, items: debouncedCart, notes: formData.specialInstructions, deliveryTime: formData.deliveryTime, deliveryMethod: formData.deliveryMethod });
    }


    const handlePayOnCheckout = () => {
        Swal.fire({
            title: t("cart").areYouSureOrderFood || "Are you sure you want to order the food?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("cart").yes || "Yes",
            cancelButtonText: t("cart").no || "No",
        }).then((result) => {
            if (result.isConfirmed) {
                bookingMutation.mutate({ bookingId, hotelId, roomId, items: debouncedCart, notes: formData.specialInstructions, deliveryTime: formData.deliveryTime, deliveryMethod: formData.deliveryMethod, payAtCheckout: true });
            }
        });

    }

    return (
        <>

            {
                showInstructions ? (
                    <Instructions setShowCheckout={setShowCheckout} setShowInstructions={setShowInstructions} formData={formData} setFormData={setFormData} />
                ) : (<div className="lg:py-[100px] md:py-[70px] sm:py-[50px] py-[30px]">
                    <div className="container">
                        <div className="grid xl:grid-cols-[669px_1fr] lg:grid-cols-2 grid-cols-1  lg:gap-[54px] gap-[20px]">
                            <div className="w-full lg:max-w-[669px] ">
                                <div className="flex lg:gap-[74px] gap-[30px] items-center pb-[28px] border-b border-[#DDDDDD] mb-[28px]">
                                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                                        {t("cart").steps?.cart || "1.Cart"}
                                    </span>
                                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                                        {t("cart").steps?.payment || "2.Payment"}
                                    </span>
                                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                                        {t("cart").steps?.confirmation || "3.Confirmation"}
                                    </span>
                                </div>
                                {data?.data?.items?.map((itemObj, index) => {
                                    const { item, quantity, note, itemTotal } = itemObj;

                                    return (
                                        <div
                                            key={item?._id || index}
                                            className="p-3 border border-[#DDDDDD] rounded-[10px] shadow-sm mb-[15px] grid md:grid-cols-[auto_1fr] grid-cols-1 gap-[20px] md:justify-between items-center"
                                        >
                                            <div className="flex gap-[16px] items-center">
                                                <img
                                                    src={item?.images?.length > 0 ? `${item?.images[0]}` : CartItem}
                                                    alt={item?.name || "Menu Item"}
                                                    className="md:w-[117.1px] md:h-[105px] w-[70px] h-[70px] rounded-[5px] object-cover"
                                                />
                                                <div>
                                                    <h3 className="xl:text-lg text-base font-medium leading-[28px] text-black">
                                                        {item?.itemName || "N/A"}
                                                    </h3>
                                                    <p className="xl:text-lg text-base text-[#686868] leading-[28px]">
                                                        {quantity} {quantity > 1 ? (t("cart").items || "items") : (t("cart").item || "item")}
                                                    </p>
                                                    {note && (
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            {t("cart").note || "Note"}: {note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="text-right md:text-right text-center">
                                                <span className="xl:text-[22px] text-lg font-medium leading-[30px] mb-[8px] inline-block">
                                                    ${itemTotal?.toFixed(2) || "0.00"}
                                                </span>
                                                <div className="w-full md:max-w-[152px] ml-auto">
                                                    <div className="flex items-center justify-between border border-gray-300 rounded-full px-4 py-2 md:max-w-[152px]">
                                                        <button
                                                            onClick={() => decrement(item?._id)}
                                                            className="lg:text-lg text-base px-2 text-gray-700 hover:text-black"
                                                        >
                                                            –
                                                        </button>
                                                        <span className="mx-4 text-base">{quantity}</span>
                                                        <button
                                                            onClick={() => increment(item?._id)}
                                                            className="lg:text-lg text-base px-2 text-gray-700 hover:text-black"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                            </div>
                            <div className="w-full lg:max-w-[597px]   h-max  sticky top-[100px]">
                                <div className="px-[25px] pt-[25px] pb-[33px] summary-box rounded-[10px]">
                                    <h2 className="xl:text-[22px] text-lg font-semibold text-black  text-center mb-[30px]">
                                        {t("cart").orderSummary || "Order Summary"}
                                    </h2>

                                    {
                                        data?.data?.items?.map((itemObj, index) => {
                                            const { item, itemTotal } = itemObj;
                                            return <div className="flex justify-between items-center gap-[20px] mb-5 xl:text-lg text-base text-black">
                                                <p>{item?.itemName}</p>
                                                <span>₾{itemTotal}</span>
                                            </div>
                                        })
                                    }


                                    <div className="flex justify-between items-center gap-[20px] mb-5 xl:text-lg text-base text-black">
                                        <p>{t("cart").discount || "Discount"}</p>
                                        <span>- ${charges?.discountAmount}  ({charges?.discountPercentage}%)</span>
                                    </div>
                                    <div className="flex  justify-between items-center gap-[20px]  xl:text-xl text-lg text-black pt-[19px] font-medium border-t   border-[#DDDDDD] mt-[10px]">
                                        <p>{t("cart").totalBeforeTaxes || "Total before taxes"}</p>
                                        <span>₾{charges?.netAmount?.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {
                                        showCheckout ? (<>
                                            <button
                                                onClick={handlePayOnCheckout}
                                                className="w-full inline-block sm:max-w-[242px] ml-auto rounded-[2px] bg-orange-500 text-[18px] text-white font-semibold p-[19px] orange-shadow hover:bg-orange-600 md:mt-[35px] mt-[20px] me-4"
                                            >
                                                {t("cart").payOnCheckout || "Pay On Checkout"}
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                className="w-full inline-block sm:max-w-[242px] ml-auto rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold p-[19px] green-shadow hover:bg-[#209b25] md:mt-[35px] mt-[20px]"
                                            >
                                                {t("cart").payNow || "Pay Now"}
                                            </button>

                                        </>) : <button
                                            onClick={() => setShowInstructions(true)}
                                            className="w-full inline-block sm:max-w-[242px] ml-auto rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold p-[19px] green-shadow hover:bg-[#209b25] md:mt-[35px] mt-[20px]"
                                        >
                                            {t("cart").next || "Next"}
                                        </button>
                                    }

                                </div>
                            </div>
                        </div>
                    </div>
                </div>)
            }

        </>
    );
};

export default CartStep;


const Instructions = ({ setShowCheckout, setShowInstructions, formData, setFormData }) => {
    const { t } = useTranslation();

    const handleSubmit = () => {
        if (!formData.deliveryTime || !formData.deliveryMethod) {
            toast.error(t("cart").pleaseFillDeliveryDetails || "Please fill delivery details.");
            return;
        }
        setShowCheckout(true);
        setShowInstructions(false);
    };

    return (
        <div className="lg:py-[100px] md:py-[70px] sm:py-[50px] py-[30px]">
            <div className="container">
                <div className="flex lg:gap-[74px] gap-[30px] items-center pb-[28px] border-b border-[#DDDDDD] mb-[28px]">
                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                        {t("cart").steps?.cart || "1.Cart"}
                    </span>
                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                        {t("cart").steps?.payment || "2.Payment"}
                    </span>
                    <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                        {t("cart").steps?.confirmation || "3.Confirmation"}
                    </span>
                </div>
                <h2 className="xl:text-[22px] text-lg font-semibold text-black text-center mb-[20px]">
                    {t("cart").specialInstructions || "Special Instructions"}
                </h2>

                <div className="mb-5">
                    <label className="block text-base font-medium mb-2 text-[#18181B]">
                        {t("cart").specialInstructions || "Special Instructions"}
                    </label>
                    <textarea
                        placeholder={t("restaurantmenu").notesPlaceholder || 'e.g., "No pepper", "Extra cheese"'}
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                        className="w-full border border-[#DDDDDD] rounded-[8px] px-4 py-3 text-base resize-none"
                    />
                </div>

                <div className="mb-5">
                    <label className="block text-base font-medium mb-2 text-[#18181B]">
                        {t("cart").deliveryTime || "Delivery Time*"}
                    </label>
                    <input
                        type="time"
                        name="deliveryTime"
                        value={formData.deliveryTime}
                        onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                        className="w-full border border-[#DDDDDD] rounded-[8px] px-4 py-3 text-base"
                    />
                </div>

                <div className="mb-5">
                    <label className="block text-base font-medium mb-2 text-[#18181B]">
                        {t("cart").deliveryMethod || "Delivery Method*"}
                    </label>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                value="deliver to room"
                                checked={formData.deliveryMethod === "deliver to room"}
                                onChange={(e) => setFormData({ ...formData, deliveryMethod: e.target.value })}
                            />
                            {t("cart").deliverToRoom || "Deliver to Room"}
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                value="ring bell & leave at door"
                                checked={formData.deliveryMethod === "ring bell & leave at door"}
                                onChange={(e) => setFormData({ ...formData, deliveryMethod: e.target.value })}
                            />
                            {t("cart").ringBellLeaveAtDoor || "Ring Bell & Leave at Door"}
                        </label>
                    </div>
                </div>

                <div className="text-right">
                    <button
                        onClick={handleSubmit}
                        className="w-full inline-block sm:max-w-[242px] ml-auto rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold p-[19px] green-shadow hover:bg-[#209b25] md:mt-[35px] mt-[20px]"
                    >
                        {t("cart").placeOrder || "Place Order"}
                    </button>
                </div>
            </div>



        </div>
    );
}
